import { actions, raise, sendTo } from "xstate"
import * as F from "fp-ts/function"
import * as ROA from "fp-ts/ReadonlyArray"
import * as RONEA from "fp-ts/ReadonlyNonEmptyArray"
import * as O from "fp-ts/Option"
import * as ORD from "fp-ts/Ord"
import * as S from "fp-ts/string"
import * as E from "fp-ts/Either"
import { PriceErrorResponseV2, PriceResponseV2, RealSuiteNumber, SuiteNumber } from '@ssc-booking/api-contracts';
import * as Amount from '@ssc-booking/api-contracts/src/amount';
import { RequestResponseChannel } from "../../../../../_common/machines/data-load-machine"
import { ECommerceEvent, ECommerceMachineOptions, ECommerceStateNodeConfig } from "../e-commerce-machine"
import { ECommerceContext } from "../e-commerce-machine-context"
import { AvailableSuitesLoaderTypes, buildAvailableSuitesLoader } from "../data-loading/available-suites-loader-machine"
import { fareCardByKey } from "../../../read-models/fare"
import { dataLoadingConfigCommon, dataLoadingOptionsCommon } from "./common"
import { allFareSuitePricesOptions, allFareSuitePricesState } from "./all-fare-suite-prices-state"

type Context = ECommerceContext
type Events = ECommerceEvent

export const dataLoadingConfigV4 = (websocketClient: RequestResponseChannel): ECommerceStateNodeConfig => ({
    type: "parallel",
    states: {
        ...dataLoadingConfigCommon(websocketClient),
        fareSuitePrices: allFareSuitePricesState(websocketClient),
        suitePrices: {
            initial: "loading",
            states: {
                loading: {
                    entry: ["raiseSuitePricesLoading"],
                    on: {
                        ALL_FARES_SELECTABLE: { target: "loaded" },
                    },
                },
                loaded: {
                    entry: ["raiseSuitePricesLoaded"],
                    on: {
                        FARE_SUITE_PRICES_LOADING: { target: "loading" },
                    },
                },
            },
        },
        availableSuites: {
            invoke: {
                id: "availableSuitesLoader",
                src: buildAvailableSuitesLoader(websocketClient),
            },
            on: {
                FARE_SELECTED: { cond: "isNotCheckingOut", actions: ["loadAvailableSuites"] },
            },
        },
    },
})

export const dataLoadingOptionsV4: ECommerceMachineOptions = {
    guards: { ...dataLoadingOptionsCommon.guards, ...allFareSuitePricesOptions.guards },
    actions: {
        ...dataLoadingOptionsCommon.actions,
        ...allFareSuitePricesOptions.actions,
        raiseSuitePricesLoading: actions.pure(() => {
            return [raise<Context, Events>({ type: "SUITE_PRICES_LOADING" })]
        }),
        raiseSuitePricesLoaded: actions.pure((ctx) => {
            return F.pipe(
                ctx.fareCards,
                ROA.filter((x) => x.status === "good"),
                ROA.flatMap((x) => x.suitePrices),
                RONEA.fromReadonlyArray,
                O.chain(F.flow(RONEA.sort(bySuiteCategory), RONEA.group(bySuiteCategory), RONEA.fromReadonlyArray)),
                O.map(RONEA.map(F.flow(RONEA.sort(byCheapestAmount), RONEA.head))),
                O.match(F.constant([]), (prices) => [raise<Context, Events>({ type: "SUITE_PRICES_LOADED", prices })]),
            )
        }),
        loadAvailableSuites: actions.pure<Context, Events>((ctx) => {
            const fare = fareCardByKey(ctx, ctx.selectedFare?.key)
            const suiteCategory = ctx.selectedSuiteCategory
            if (!fare || !suiteCategory) return undefined

            return [
                sendTo<Context, Events, AvailableSuitesLoaderTypes["ActorRef"]>(
                    "availableSuitesLoader",
                    (ctx): AvailableSuitesLoaderTypes["Load"] => {
                        return {
                            type: "LOAD",
                            data: {
                                cruiseCode: ctx.cruiseCode,
                                occupancy: ctx.guests,
                                fareCode: fare.fare,
                                suiteCategory,
                                preferredSuiteNumber: F.pipe(
                                    O.fromNullable(ctx.preferredSuiteNumber),
                                    O.chain(O.fromPredicate((x): x is RealSuiteNumber => x !== SuiteNumber.guaranteed)),
                                ),
                            },
                            country: ctx.geoLocation.countryCode,
                        }
                    },
                ),
            ]
        }),
    },
}

const bySuiteCategory = F.pipe(
    S.Ord,
    ORD.contramap(
        E.match(
            PriceErrorResponseV2.lensFromPath(["request", "suiteCategory"]).get,
            PriceResponseV2.lensFromProp("suiteCategory").get,
        ),
    ),
)
const byCheapestAmount = F.pipe(
    Amount.cheapest,
    ORD.contramap(
        E.match(
            (_: PriceErrorResponseV2) => Amount.of(Number.MAX_VALUE, "USD"),
            PriceResponseV2.lensFromPath(["price", "total"]).get,
        ),
    ),
)
